/**
 * Sweepstakes entry counter: computes "how many entries will this cart
 * earn" from live cart contents and the sitewide product-multiplier map
 * rendered by snippets/sweepstakes-multiplier-data.liquid, then renders the
 * total into every [data-sweepstakes-entry-count] element on the page.
 *
 * Entries = whole dollars actually paid + a flat per-unit bonus per product
 * (sweepstakes.bonus_entries, mirrored from what the team entered in
 * ViralSweep), the same formula ViralSweep applies. The multiplier metafield
 * (2x on accessories etc.) only drives the "Nx entries" badge wording now.
 *
 * This is a display-only estimate for the storefront, not the sweepstakes'
 * authoritative entry ledger — ViralSweep (going live separately) owns that.
 *
 * The whole file is a no-op outside the US/Canada markets the campaign is
 * scoped to. window.CM_SWEEPSTAKES_MARKET_OK is set once, sitewide, by
 * snippets/sweepstakes-multiplier-data.liquid (the same file that owns the
 * market check via snippets/sweepstakes-market-gate.liquid) — checked here
 * too, not just in Liquid, because the SlideCart drawer widget below has no
 * server-rendered markup of its own to gate; it's pure JS DOM injection.
 */
(function () {
  if (!window.CM_SWEEPSTAKES_MARKET_OK) return;

  function getMultiplierMap() {
    return window.CM_SWEEPSTAKES_MULTIPLIERS || {};
  }

  /*
   * The official rules award entries on whole dollars spent in USD. Shoppers
   * in other markets (Canada pays CAD) see presentment prices, so the math
   * must convert back to USD. Appending ?country=US to a product's .js URL
   * returns its USD variant prices whatever market the shopper is in; each
   * handle is fetched once per page. A line's USD paid amount is its paid
   * amount scaled by (USD unit price / presentment unit price), which keeps
   * discounts (Buy More Save More) proportional.
   */
  function currentCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  }

  function isUsdContext() {
    var c = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    return !c || c === 'USD';
  }

  var usdCache = {};
  function usdPrices(handle) {
    if (!usdCache[handle]) {
      usdCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js?country=US', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          var m = {};
          ((p && p.variants) || []).forEach(function (v) { m[v.id] = v.price; });
          return m;
        })
        .catch(function () { return {}; });
    }
    return usdCache[handle];
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  function effectiveMultiplier(productId, map) {
    var entry = map[String(productId)];
    if (!entry) return 1;
    if (entry.promoMultiplier != null && entry.promoStartsAt != null && entry.promoEndsAt != null) {
      var now = Date.now() / 1000;
      if (now >= entry.promoStartsAt && now <= entry.promoEndsAt) {
        return entry.promoMultiplier;
      }
    }
    return entry.multiplier != null ? entry.multiplier : 1;
  }

  /*
   * ViralSweep's own formula, so the site never promises a different number
   * than the ledger awards: whole dollars actually PAID (after discounts, so
   * Buy More Save More lowers it; shipping and tax are not in item lines) plus
   * a flat per-unit bonus per product, entered by hand in ViralSweep and
   * mirrored here from the sweepstakes.bonus_entries metafield. Variant ids
   * are looked up first so mapped variants (Insta360 X-series) get their own
   * bonus. Cents are floored once on the whole item subtotal.
   */
  function bonusFor(map, variantId, productId) {
    var entry = map[String(variantId)] || map[String(productId)];
    return entry && entry.bonus ? entry.bonus : 0;
  }

  /*
   * Order-value milestone ("spend $X, earn N bonus entries"). Counted on whole USD of order value
   * after discounts, same basis as the entries themselves. Each offer below is one scheduled Order Bonus period in
   * ViralSweep; the drawer bar, the cart counter, the PDP pill and the copy all read from the offer that is active
   * right now, so the site flips on its own at the boundary with no push. The bonus must also be awarded in
   * ViralSweep (Order Bonus, Schedule Order Bonus Period), or the site would promise entries the ledger doesn't give.
   * Times are Atlantic/Eastern (UTC-4 until Nov 1, then UTC-5). Outside every window the bar hides and no bonus is counted.
   */
  var AOV_OFFERS = [
    { startsAt: '2026-10-08T00:00:00-04:00', endsAt: '2026-10-13T23:59:00-04:00', threshold: 75, bonus: 300 },
    { startsAt: '2026-10-14T00:00:00-04:00', endsAt: '2026-10-20T23:59:00-04:00', threshold: 100, bonus: 500 },
    { startsAt: '2026-10-21T00:00:00-04:00', endsAt: '2026-10-27T23:59:00-04:00', threshold: 75, bonus: 500 },
    { startsAt: '2026-10-28T00:00:00-04:00', endsAt: '2026-11-03T23:59:00-05:00', threshold: 70, bonus: 500 }
  ];
  // Set by aovInWindow() to the active offer. Defaults to the latest offer so copy still reads sensibly outside a window.
  var AOV_THRESHOLD_USD = AOV_OFFERS[AOV_OFFERS.length - 1].threshold;
  var AOV_BONUS_ENTRIES = AOV_OFFERS[AOV_OFFERS.length - 1].bonus;
  // Currencies and countries the offer is shown and counted in. Canada is left out until ViralSweep's
  // handling of CAD orders is confirmed (does it compare the raw CAD number to the threshold, or convert
  // to USD first?). To switch Canada on, add 'CAD' to AOV_CURRENCIES and 'CA' to AOV_COUNTRIES.
  var AOV_CURRENCIES = ['USD'];
  // The shopper's country must also be on this list. The cart's own currency can lag behind the page
  // (a Canadian visitor can still hold a USD cart), so currency alone is not a safe gate.
  var AOV_COUNTRIES = ['US'];
  function aovInWindow() {
    var now = Date.now();
    for (var i = 0; i < AOV_OFFERS.length; i++) {
      var o = AOV_OFFERS[i];
      if (now >= Date.parse(o.startsAt) && now <= Date.parse(o.endsAt)) {
        AOV_THRESHOLD_USD = o.threshold;
        AOV_BONUS_ENTRIES = o.bonus;
        return true;
      }
    }
    return false;
  }
  function aovEnabledFor(currency) {
    var country = (window.Shopify && window.Shopify.country) || '';
    return aovInWindow() && AOV_CURRENCIES.indexOf(currency || 'USD') !== -1 && AOV_COUNTRIES.indexOf(country) !== -1;
  }

  function computeCart(cart) {
    var map = getMultiplierMap();
    var usd = (cart.currency || 'USD') === 'USD';
    return Promise.all((cart.items || []).map(function (item) {
      var lineCents = item.final_line_price != null ? item.final_line_price : item.line_price;
      var bonus = bonusFor(map, item.variant_id, item.product_id) * item.quantity;
      if (usd) return { paid: lineCents, pres: lineCents, bonus: bonus };
      return usdPrices(item.handle).then(function (prices) {
        var usdUnit = prices[item.variant_id];
        var presUnit = item.original_price;
        var paid = usdUnit != null && presUnit ? lineCents * usdUnit / presUnit : lineCents;
        return { paid: paid, pres: lineCents, bonus: bonus };
      });
    })).then(function (rows) {
      var paidCents = 0, presCents = 0, bonus = 0;
      rows.forEach(function (r) { paidCents += r.paid; presCents += r.pres; bonus += r.bonus; });
      var usdPaid = Math.round(paidCents) / 100;
      var dollars = Math.floor(usdPaid);
      var aovOn = aovEnabledFor(cart.currency);
      var aovUnlocked = aovOn && dollars >= AOV_THRESHOLD_USD;
      return {
        entries: dollars + bonus + (aovUnlocked ? AOV_BONUS_ENTRIES : 0),
        usdPaid: usdPaid,
        // shopper-currency units per USD, to word "add $X more" in the currency they see
        rate: usdPaid > 0 ? (presCents / 100) / usdPaid : 1,
        currency: cart.currency || 'USD',
        itemCount: cart.item_count || 0,
        aovEnabled: aovOn,
        aovUnlocked: aovUnlocked
      };
    });
  }

  function formatMoney(amount, currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency, currencyDisplay: 'narrowSymbol' })
        .format(amount).replace(/([.,])00(?!\d)/, '');
    } catch (e) {
      return '$' + amount.toFixed(2).replace(/\.00$/, '');
    }
  }

  function renderAov(state) {
    document.querySelectorAll('[data-sweepstakes-aov]').forEach(function (el) {
      var hide = state.itemCount <= 0 || !state.aovEnabled;
      if (el.hidden !== hide) el.hidden = hide;
      if (hide) { var t0 = el.querySelector('[data-sweepstakes-aov-text]'); if (t0 && t0.textContent) t0.textContent = ''; return; }
      var pct = Math.max(0, Math.min(100, state.usdPaid / AOV_THRESHOLD_USD * 100));
      var fill = el.querySelector('[data-sweepstakes-aov-fill]');
      if (fill) fill.style.width = pct.toFixed(1) + '%';
      var ticket = el.querySelector('.sweepstakes-aov__ticket');
      if (ticket) ticket.style.left = pct.toFixed(1) + '%';
      el.classList.toggle('is-unlocked', state.aovUnlocked);
      var bonusWord = AOV_BONUS_ENTRIES === 1 ? 'bonus entry' : 'bonus entries';
      var text = el.querySelector('[data-sweepstakes-aov-text]');
      if (!text) return;
      var html;
      if (state.aovUnlocked) {
        html = '<b>+' + AOV_BONUS_ENTRIES + ' ' + bonusWord + ' unlocked!</b> It\'s included in your entries.';
      } else {
        // Round the gap up to the cent so "add $X more" always crosses the line.
        var left = Math.ceil((AOV_THRESHOLD_USD - state.usdPaid) * state.rate * 100) / 100;
        html = 'Add <b>' + formatMoney(left, state.currency) + '</b> more to earn <b>+' + AOV_BONUS_ENTRIES + ' ' + bonusWord + '</b>';
      }
      if (text.innerHTML !== html) text.innerHTML = html;
    });
  }

  function render(state) {
    var entries = state.entries;
    document.querySelectorAll('[data-sweepstakes-entry-count]').forEach(function (el) {
      el.textContent = entries.toLocaleString();
    });
    document.querySelectorAll('[data-sweepstakes-entry-count-widget]').forEach(function (el) {
      el.hidden = entries <= 0;
    });
    renderAov(state);
  }

  // USD lookups make computeCart asynchronous, so two quick cart changes
  // (add, then the drawer's own refresh) can finish out of order. Only the
  // newest request is allowed to render.
  var refreshSeq = 0;
  function refresh() {
    var mine = ++refreshSeq;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) { return computeCart(cart); })
      .then(function (state) { if (mine === refreshSeq) render(state); })
      .catch(function () {
        /* Cart fetch failing shouldn't break the page — leave whatever was last rendered. */
      });
  }

  window.CM_SWEEPSTAKES_REFRESH_ENTRIES = refresh;

  document.addEventListener('DOMContentLoaded', refresh);

  /*
   * The buy-boxes on this theme (bmsm-buybox.liquid, chin-mount-bmsm.liquid,
   * chin-mount-product.liquid's "bmsm" branch) add to cart via a plain
   * fetch('/cart/add.js', ...) call from their own addKit() function, not a
   * <form action="/cart/add"> submit — so a click listener on a submit
   * button never fires here. Patching window.fetch itself, the same
   * technique this theme's own Klaviyo "Added to Cart" script (in
   * layout/theme.liquid) already uses to detect cart/add.js calls, catches
   * every add-to-cart path on the site regardless of which buy-box variant
   * triggered it, including cart-quantity changes via cart/change.js.
   */
  (function (fetch) {
    window.fetch = function () {
      var response = fetch.apply(this, arguments);
      var url = arguments[0] instanceof Request ? arguments[0].url : arguments[0];
      if (typeof url === 'string' && /\/cart\/(add|change|update|clear)\.js/.test(url)) {
        response.then(function () { refresh(); });
      }
      return response;
    };
  })(window.fetch);

  /*
   * The SlideCart drawer (a third-party app) removes items and changes
   * quantities with old-style XMLHttpRequest calls to /cart/change.js, which
   * the fetch patch above never sees, so the counter stayed stale after a
   * removal. Watch XHR cart calls too and refresh once they finish.
   */
  (function () {
    var CART_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    var open = XMLHttpRequest.prototype.open;
    var send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__cmCartCall = CART_RE.test(String(url));
      return open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.__cmCartCall) {
        this.addEventListener('loadend', function () { refresh(); });
      }
      return send.apply(this, arguments);
    };
  })();

  /*
   * PDP entry teaser for the BMSM buy-box (sections/bmsm-buybox.liquid):
   * makes "Purchase for N entries" react live as the shopper checks/unchecks
   * accessory rows, and labels each boosted accessory row with its own
   * "Nx entries" badge — instead of the snippet's static, product.price-only
   * server render.
   *
   * The buy-box's own refresh() is a closure-private function (there are two
   * of them in that file, one per "bmsm"/"spd" markup flavor it can render),
   * so rather than reaching into that logic, this watches its OUTPUT:
   * refresh() always finishes by writing the new total into
   * [data-bmsm-price] and each visible row's [data-now], synchronously in
   * one pass, so a single MutationObserver on the buy-box root — firing only
   * once the DOM has settled — sees the final state after every
   * brand/model/variant/accessory change with no extra event wiring, and
   * without any risk of touching the buy-box's carefully-tuned pricing math.
   *
   * Only the "bmsm" flavor is handled ([data-bmsm-price] present); "spd"
   * rows don't expose a tier-adjusted per-row price to read, so those PDPs
   * keep the static snippet value.
   */
  function parseMoney(text) {
    var n = parseFloat(String(text || '').replace(/[^0-9.-]/g, ''));
    return isNaN(n) ? 0 : n;
  }

  /*
   * Shared PDP renderer: the single place that writes the live entry total.
   * Fills the badge under the price (count, label, and a short-lived "+30" / "-12" chip showing
   * what the last click changed), the matching line above the Add to cart
   * button, and the "In Your Kit" drawer on phones. Everything is derived from
   * numbers the caller already computed, and every write is skipped when the
   * value is unchanged so the buy-box MutationObserver never loops.
   */
  var lastShownEntries = null;
  var deltaTimer = null;
  var ENTRIES_ICON = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true"><path d="M22 10V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v4a2 2 0 0 1 0 4v4a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-4a2 2 0 0 1 0-4ZM13.5 17.5h-3v-2h3Zm0-4.5h-3v-2h3Zm0-4.5h-3v-2h3Z" fill="currentColor"/></svg>';

  function bump(el) {
    if (!el) return;
    el.classList.remove('is-bump');
    void el.offsetWidth;
    el.classList.add('is-bump');
    setTimeout(function () { el.classList.remove('is-bump'); }, 500);
  }

  function ensureAtcLine() {
    var atc = document.querySelector('[data-bmsm-atc]');
    if (!atc || !atc.parentNode) return null;
    var line = document.querySelector('[data-sweepstakes-atcline]');
    if (!line) {
      line = document.createElement('div');
      line.className = 'sweepstakes-pdp-atcline';
      line.setAttribute('data-sweepstakes-atcline', '');
      line.hidden = true;
      line.innerHTML = ENTRIES_ICON + '<span>This order earns <strong data-sweepstakes-atcline-count>0</strong> <span data-sweepstakes-atcline-label>entries</span></span>';
      atc.parentNode.insertBefore(line, atc);
    }
    return line;
  }

  function ensureDrawerChip() {
    var host = document.querySelector('[data-bmsm-drawer-toggle] > span');
    if (!host) return null;
    var chip = host.querySelector('[data-sweepstakes-drawer-entries]');
    if (!chip) {
      chip = document.createElement('span');
      chip.setAttribute('data-sweepstakes-drawer-entries', '');
      chip.hidden = true;
      host.appendChild(chip);
    }
    return chip;
  }

  function renderPdpEntries(teaser, entries, dollars, bonus, aov) {
    var countEl = teaser.querySelector('[data-sweepstakes-pdp-entries-count]');
    var labelEl = teaser.querySelector('[data-sweepstakes-pdp-entries-label]');
    var deltaEl = teaser.querySelector('[data-sweepstakes-pdp-delta]');
    var word = entries === 1 ? 'entry' : 'entries';
    var changed = lastShownEntries !== null && entries !== lastShownEntries;

    setText(countEl, entries.toLocaleString());
    setText(labelEl, word);

    var shouldHide = entries <= 0;
    if (teaser.hidden !== shouldHide) teaser.hidden = shouldHide;

    // Gold half of the pill: plain "this order", or the order-value reward once the kit crosses the threshold.
    var tagEl = teaser.querySelector('[data-sweepstakes-pdp-tag]');
    if (tagEl) setText(tagEl, aov ? 'includes ' + AOV_BONUS_ENTRIES.toLocaleString() + ' bonus' : 'this order');

    var atc = document.querySelector('[data-bmsm-atc]');
    var line = ensureAtcLine();
    if (line) {
      setText(line.querySelector('[data-sweepstakes-atcline-count]'), entries.toLocaleString());
      setText(line.querySelector('[data-sweepstakes-atcline-label]'), word);
      // The button reads "Select your camera to continue" until a kit exists; no total to promise yet.
      var hideLine = shouldHide || !!(atc && atc.disabled);
      if (line.hidden !== hideLine) line.hidden = hideLine;
    }

    var chip = ensureDrawerChip();
    if (chip) {
      setText(chip, ' \u00b7 ' + entries.toLocaleString() + ' ' + word);
      var hideChip = shouldHide || !!(atc && atc.disabled);
      if (chip.hidden !== hideChip) chip.hidden = hideChip;
    }

    if (changed) {
      bump(countEl);
      bump(line && line.querySelector('strong'));
      if (deltaEl) {
        var d = entries - lastShownEntries;
        deltaEl.textContent = (d > 0 ? '+' : '-') + Math.abs(d).toLocaleString();
        deltaEl.classList.toggle('is-neg', d < 0);
        deltaEl.hidden = false;
        clearTimeout(deltaTimer);
        // The minus flash goes away faster than the plus flash: it was pulling more attention than the total.
        deltaTimer = setTimeout(function () { deltaEl.hidden = true; }, d < 0 ? 2000 : 3500);
      }
    }
    lastShownEntries = entries;
  }

  /*
   * Accessory rows get a plain "Bonus entries" label with no number. The real total depends on
   * the Buy More Save More tier the shopper ends up in, so the live badge under the price (and the
   * green change chip) are the only places that show a count.
   */
  function injectRowEntryBadges(box, map) {
    if (!box) return;
    box.querySelectorAll('[data-vid]').forEach(function (row) {
      if (row.querySelector('[data-sweepstakes-row-badge]')) return;
      var rowEntry = map[String(row.getAttribute('data-vid'))];
      if (!rowEntry || !rowEntry.bonus || rowEntry.bonus <= 0) return;
      var nameEl = row.querySelector('.bmsm-reqrow__name');
      if (!nameEl) return;
      var badge = document.createElement('span');
      badge.className = 'bmsm-reqrow__entries';
      badge.setAttribute('data-sweepstakes-row-badge', '');
      badge.textContent = 'Bonus entries';
      nameEl.appendChild(document.createTextNode(' '));
      nameEl.appendChild(badge);
    });
  }

  function recomputeBmsmTeaser(root, priceEl, teaser) {
    var map = getMultiplierMap();
    // overallTotal is the buy-box's own live total: the mount plus every
    // checked accessory row at tier-discounted prices, i.e. what is actually
    // paid (in the shopper's currency). Entries = whole USD dollars paid +
    // flat bonuses for each boosted item, same as the cart.
    var overallTotal = parseMoney(priceEl.textContent);
    var bonus = 0;
    var rowsTierTotal = 0;
    var rows = [];
    root.querySelectorAll('[data-bmsm-req] [data-vid], [data-bmsm-opts] [data-vid]').forEach(function (row) {
      var cb = row.querySelector('input[data-req-vid], input[data-opt-vid]');
      if (!cb || !cb.checked) return;
      var vid = row.getAttribute('data-vid');
      var entry = map[String(vid)];
      var tier = parseMoney((row.querySelector('[data-now]') || {}).textContent);
      rowsTierTotal += tier;
      if (entry && entry.bonus) bonus += entry.bonus;
      rows.push({ vid: vid, tier: tier, entry: entry });
    });
    // The free gift ($0, added to the cart once the kit unlocks it) still earns its flat bonus
    // (ViralSweep and the cart counter both count $0 lines), so include it, unless its own
    // accessory row is already ticked and counted above. The buy boxes tag the gift block
    // with its variant id (data-gift-id) and flip .is-unlocked on it.
    var giftEl = root.querySelector('[data-bmsm-gift]');
    if (giftEl && giftEl.classList.contains('is-unlocked')) {
      var giftId = giftEl.getAttribute('data-gift-id');
      var giftEntry = giftId ? map[String(giftId)] : null;
      var giftCounted = rows.some(function (r) { return String(r.vid) === String(giftId); });
      if (giftEntry && giftEntry.bonus && !giftCounted) bonus += giftEntry.bonus;
    }
    // The always-free default gift (Creator Pack on chin mount PDPs) is a locked, ticked row
    // ([data-giftrow]) that appears once the shopper picks a camera, and the buy box adds it to the cart
    // at $0. It earns its flat bonus like any other line (the cart counter and ViralSweep both count it),
    // so the pill adds it too, unless the same variant is already counted from a ticked accessory row.
    root.querySelectorAll('[data-giftrow]').forEach(function (gr) {
      var gvid = gr.getAttribute('data-gift-vid');
      var gentry = gvid ? map[String(gvid)] : null;
      var already = rows.some(function (r) { return String(r.vid) === String(gvid); });
      if (gentry && gentry.bonus && !already) bonus += gentry.bonus;
    });
    // On VisePro Max's or Flexible Mount's own PDP the main product is itself
    // a boosted item, so its bonus counts too.
    // The mount multi-pack picker (1 / 2 / 3 mounts, snippets/cm-multipack.liquid) adds that many
    // units of the main product to the cart, and each unit earns its own bonus.
    var mainId = root.getAttribute('data-bmsm-main-id');
    var mainEntry = mainId ? map[String(mainId)] : null;
    var packInput = root.querySelector('[data-cm-multipack] input[name="cm-pack"]:checked');
    var mainQty = Math.max(1, parseInt(packInput && packInput.value, 10) || 1);
    if (mainEntry && mainEntry.bonus) bonus += mainEntry.bonus * mainQty;
    var mainTier = overallTotal - rowsTierTotal;

    function finish(usdTotal) {
      var dollars = Math.floor(Math.round(usdTotal * 100) / 100);
      var aov = aovEnabledFor(currentCurrency()) && dollars >= AOV_THRESHOLD_USD;
      renderPdpEntries(teaser, dollars + bonus + (aov ? AOV_BONUS_ENTRIES : 0), dollars, bonus, aov);
    }

    if (isUsdContext()) { finish(overallTotal); return; }

    var parts = rows.map(function (r) {
      var handle = r.entry && r.entry.handle;
      var presBase = r.entry && r.entry.price ? r.entry.price / 100 : 0;
      if (!handle || !presBase) return Promise.resolve(r.tier);
      return usdPrices(handle).then(function (m) {
        var u = m[r.vid];
        return u != null ? r.tier * (u / 100) / presBase : r.tier;
      });
    });
    var mHandle = teaser.getAttribute('data-product-handle');
    var mVid = teaser.getAttribute('data-variant-id');
    var mPres = (parseInt(teaser.getAttribute('data-variant-price'), 10) || 0) / 100;
    parts.push(mHandle && mPres ? usdPrices(mHandle).then(function (m) {
      var u = m[mVid];
      return u != null ? mainTier * (u / 100) / mPres : mainTier;
    }) : Promise.resolve(mainTier));
    Promise.all(parts).then(function (vals) {
      finish(vals.reduce(function (a, b) { return a + b; }, 0));
    });
  }

  function bindBmsmRoot(root) {
    if (root.__cmSwpBound) return;
    var priceEl = root.querySelector('[data-bmsm-price]');
    var teaser = root.querySelector('[data-sweepstakes-pdp-teaser]');
    if (!priceEl || !teaser) return;
    root.__cmSwpBound = true;

    // refreshAll() itself writes into `root` (badges, the teaser's own
    // count/label text) — without disconnecting first, those writes would
    // re-trigger this same observer forever, since even re-assigning
    // .textContent to an unchanged string still replaces the text node.
    var observerOptions = { childList: true, subtree: true, characterData: true };
    var observer = new MutationObserver(function () {
      observer.disconnect();
      refreshAll();
      observer.observe(root, observerOptions);
    });

    function refreshAll() {
      var map = getMultiplierMap();
      injectRowEntryBadges(root.querySelector('[data-bmsm-req]'), map);
      injectRowEntryBadges(root.querySelector('[data-bmsm-opts]'), map);
      recomputeBmsmTeaser(root, priceEl, teaser);
    }

    refreshAll();
    observer.observe(root, observerOptions);
  }

  // Every buy box on the page, including ones added after load: the sweepstakes landing page's helmet finder
  // builds its buy box only after the shopper picks a helmet, long after DOMContentLoaded.
  function setupBmsmTeaser() {
    document.querySelectorAll('.bmsm[data-bmsm-root]:not([data-cm-swp-bound])').forEach(bindBmsmRoot);
  }
  var bmsmScanTimer;
  function scheduleBmsmScan() {
    clearTimeout(bmsmScanTimer);
    bmsmScanTimer = setTimeout(function () {
      document.querySelectorAll('.bmsm[data-bmsm-root]').forEach(bindBmsmRoot);
    }, 150);
  }
  if ('MutationObserver' in window) {
    document.addEventListener('DOMContentLoaded', function () {
      new MutationObserver(scheduleBmsmScan).observe(document.body, { childList: true, subtree: true });
    });
  }

  document.addEventListener('DOMContentLoaded', setupBmsmTeaser);
  /*
   * Standard (non-BMSM) PDPs: the server-rendered teaser is for the first
   * variant at quantity 1. When the shopper picks another variant (different
   * price, and for the Insta360 X-series mount a different mapped bonus) or
   * quantity, recompute from the same map: whole dollars + flat bonus, per
   * unit times quantity.
   */
  function setupPlainTeaser() {
    var teaser = document.querySelector('[data-sweepstakes-pdp-teaser]');
    if (!teaser || document.querySelector('.bmsm[data-bmsm-root]')) return;
    var form = document.querySelector('form[action*="/cart/add"]');
    var idEl = form && form.querySelector('[name="id"]');
    if (!idEl) return;
    var handle = teaser.getAttribute('data-product-handle');
    var qtyEl = form.querySelector('[name="quantity"], #product-quantity-select, [name="product-quantity-select"]');

    // Unit price in USD cents for the selected variant: the map's price when
    // the shopper is already in USD, else looked up through ?country=US.
    function usdUnitPrice(vid, entry) {
      if (isUsdContext()) return Promise.resolve(entry && entry.price != null ? entry.price : null);
      if (!handle) return Promise.resolve(null);
      return usdPrices(handle).then(function (m) { return m[vid] != null ? m[vid] : null; });
    }

    function update() {
      var vid = idEl.value;
      var entry = getMultiplierMap()[String(vid)];
      usdUnitPrice(vid, entry).then(function (price) {
        if (price == null || vid !== idEl.value) return;
        var qty = Math.max(1, parseInt(qtyEl && qtyEl.value, 10) || 1);
        var dollars = Math.floor((price * qty) / 100);
        var bonus = ((entry && entry.bonus) || 0) * qty;
        var aov = aovEnabledFor(currentCurrency()) && dollars >= AOV_THRESHOLD_USD;
        renderPdpEntries(teaser, dollars + bonus + (aov ? AOV_BONUS_ENTRIES : 0), dollars, bonus, aov);
      });
    }

    form.addEventListener('change', function () { setTimeout(update, 50); });
    form.addEventListener('click', function () { setTimeout(update, 150); });
    update();
  }

  document.addEventListener('DOMContentLoaded', setupPlainTeaser);

  /*
   * Collection grid pills ("35 entries / this order") on product cards. In USD the number is rendered by Liquid.
   * In other markets (Canada) the card is rendered hidden with data-needs-usd; here we look up the product's USD price
   * (the same ?country=US lookup the product page uses), fill in the whole-dollar count and reveal the pill. Lookups run a few at a time,
   * and cards added later (filters, load more) are picked up too.
   */
  function fillGridPills() {
    var queue = Array.prototype.slice.call(document.querySelectorAll('[data-cm-grid-pill][data-needs-usd]')).filter(function (p) { return !p.__cmGridBusy; });
    if (!queue.length) return;
    queue.forEach(function (p) { p.__cmGridBusy = true; });
    function fill(pill) {
      return usdPrices(pill.getAttribute('data-handle')).then(function (m) {
        var cents = Object.keys(m).map(function (k) { return m[k]; }).filter(function (n) { return n > 0; });
        if (!cents.length) { pill.__cmGridBusy = false; return; }
        var dollars = Math.floor(Math.round(Math.min.apply(null, cents)) / 100);
        var countEl = pill.querySelector('[data-cm-grid-count]');
        if (countEl) countEl.textContent = String(dollars);
        pill.removeAttribute('data-needs-usd');
        pill.hidden = false;
      });
    }
    // A few lookups at a time so a 48-card page doesn't fire 48 requests at once.
    function worker() {
      var next = queue.shift();
      if (!next) return Promise.resolve();
      return fill(next).then(worker, worker);
    }
    for (var i = 0; i < 6; i++) worker();
  }
  var gridTimer;
  function scheduleGridPills() { clearTimeout(gridTimer); gridTimer = setTimeout(fillGridPills, 250); }
  if (!isUsdContext()) {
    document.addEventListener('DOMContentLoaded', scheduleGridPills);
    if (document.readyState !== 'loading') scheduleGridPills();
    if ('MutationObserver' in window) {
      new MutationObserver(scheduleGridPills).observe(document.documentElement, { childList: true, subtree: true });
    }
  }

})();

/*
 * "N entries in your cart" inside the SlideCart app drawer (#slidecarthq).
 * SlideCart is a third-party theme app extension (cdn.shopify.com/extensions/
 * .../slide-cart-1-128/), not this theme's own markup, so it's the actual
 * cart UI shoppers see on every add-to-cart (sections/static-cart.liquid,
 * the full /cart page, is rarely if ever reached in practice) — the
 * sweepstakes-entry-counter.liquid render there never shows up here.
 *
 * SlideCart's drawer contents re-render from scratch on every cart change
 * (its class-hash CSS points to a React-style component), wiping any DOM
 * node inserted into it — so rather than inserting once, this re-inserts
 * every time its own MutationObserver sees the drawer change, self-healing
 * after each of SlideCart's own re-renders. It reuses the exact same
 * [data-sweepstakes-entry-count] / [data-sweepstakes-entry-count-widget]
 * hooks and CM_SWEEPSTAKES_REFRESH_ENTRIES the cart-page counter uses, so
 * the actual entries math lives in exactly one place.
 */
(function () {
  if (!window.CM_SWEEPSTAKES_MARKET_OK) return;

  var WIDGET_HTML =
    '<div class="sweepstakes-entry-counter" data-sweepstakes-entry-count-widget hidden>' +
    '<div class="swp-cart-pill"><span class="swp-cart-pill__main"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true"><path d="M22 10V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v4a2 2 0 0 1 0 4v4a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-4a2 2 0 0 1 0-4ZM13.5 17.5h-3v-2h3Zm0-4.5h-3v-2h3Zm0-4.5h-3v-2h3Z" fill="currentColor"/></svg><strong data-sweepstakes-entry-count>0</strong><span class="swp-cart-pill__label">entries</span></span><span class="swp-cart-pill__tag">in your cart</span></div>' +
    '</div>';

  var AOV_HTML =
    '<div class="sweepstakes-aov" data-sweepstakes-aov hidden>' +
    '<p class="sweepstakes-aov__text" data-sweepstakes-aov-text></p>' +
    '<div class="sweepstakes-aov__track"><span class="sweepstakes-aov__fill" data-sweepstakes-aov-fill></span>' +
    '<span class="sweepstakes-aov__ticket"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true"><path d="M22 10V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v4a2 2 0 0 1 0 4v4a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-4a2 2 0 0 1 0-4ZM13.5 17.5h-3v-2h3Zm0-4.5h-3v-2h3Zm0-4.5h-3v-2h3Z" fill="currentColor"/></svg></span></div>' +
    '</div>';

  function ensureStyle() {
    if (document.getElementById('sweepstakes-entry-counter-style')) return;
    var style = document.createElement('style');
    style.id = 'sweepstakes-entry-counter-style';
    // The cart drawer app styles its own divs and spans aggressively, so layout rules here are forced.
    var forced = [
      '#slidecarthq .sweepstakes-aov[hidden]{display:none;}',
      '#slidecarthq .sweepstakes-aov:not([hidden]){display:block;margin:4px 20px 14px;padding:12px 14px 14px;border-radius:12px;background:#fff8ec;box-shadow:0 0 0 1px rgba(201,150,43,.35);text-align:center;}',
      '#slidecarthq .sweepstakes-aov__text{margin:0 0 10px;font-size:13px;line-height:1.3;font-weight:600;color:#3a2600;}',
      '#slidecarthq .sweepstakes-aov__text b{font-weight:800;color:#8a5a00;}',
      '#slidecarthq .sweepstakes-aov__track{position:relative;display:block;height:10px;border-radius:999px;background:#eadfc6;margin:0 13px;}',
      '#slidecarthq .sweepstakes-aov__fill{display:block;height:100%;border-radius:999px;background:linear-gradient(90deg,#f3cf7a,#c99422);transition:width .35s ease;}',
      '#slidecarthq .sweepstakes-aov__ticket{position:absolute;top:50%;margin-left:-13px;margin-top:-13px;transition:left .35s ease;display:flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:50%;background:#0a1016;color:#ffd978;box-shadow:0 0 0 2px #fff8ec;}',
      '#slidecarthq .sweepstakes-aov.is-unlocked{background:#f2fbf6;box-shadow:0 0 0 1px rgba(31,157,85,.45);}',
      '#slidecarthq .sweepstakes-aov.is-unlocked .sweepstakes-aov__text,#slidecarthq .sweepstakes-aov.is-unlocked .sweepstakes-aov__text b{color:#17653a;}',
      '#slidecarthq .sweepstakes-aov.is-unlocked .sweepstakes-aov__fill{background:linear-gradient(90deg,#5fcf8f,#1f9d55);}'
    ].join('').replace(/;/g, ' !important;');
    style.textContent =
      '.sweepstakes-entry-counter[hidden],.sweepstakes-aov[hidden]{display:none !important;}' +
      '.sweepstakes-entry-counter:not([hidden]){display:flex;justify-content:center;margin:14px 20px;}' +
      '.swp-cart-pill{display:inline-flex;align-items:stretch;border-radius:11px;overflow:hidden;box-shadow:0 6px 18px rgba(201,150,43,.38),0 0 0 1px rgba(255,217,120,.35);font-style:italic;font-weight:800;text-transform:uppercase;letter-spacing:.02em;line-height:1;}' + '#slidecarthq .swp-cart-pill{display:inline-flex!important;align-items:stretch!important;border-radius:11px!important;overflow:hidden!important;box-shadow:0 6px 18px rgba(201,150,43,.38),0 0 0 1px rgba(255,217,120,.35)!important;font-style:italic!important;font-weight:800!important;text-transform:uppercase!important;letter-spacing:.02em!important;line-height:1!important;}#slidecarthq .swp-cart-pill__main{display:flex!important;align-items:center!important;gap:8px!important;padding:12px 24px 12px 14px!important;color:#fff!important;background:linear-gradient(135deg,#0a1016 0%,#1d2b36 100%)!important;}#slidecarthq .swp-cart-pill__main svg{flex:0 0 auto!important;color:#ffd978!important;}#slidecarthq .swp-cart-pill__main strong{font-size:22px!important;font-weight:900!important;color:#ffd978!important;}#slidecarthq .swp-cart-pill__label{font-size:13px!important;}#slidecarthq .swp-cart-pill__tag{display:flex!important;align-items:center!important;margin-left:-13px!important;padding:0 16px 0 27px!important;font-size:13px!important;font-weight:900!important;color:#3a2600!important;background:linear-gradient(135deg,#fde8a6 0%,#e9bb4b 52%,#c99422 100%)!important;clip-path:polygon(13px 0,100% 0,100% 100%,0 100%)!important;white-space:nowrap!important;}' + forced;
    document.head.appendChild(style);
  }

  function injectIntoDrawer() {
    var header = document.querySelector('#slidecarthq .slidecarthq > .header');
    if (!header) return;
    var counter = header.nextElementSibling;
    var changed = false;
    if (!(counter && counter.hasAttribute('data-sweepstakes-entry-count-widget'))) {
      ensureStyle();
      header.insertAdjacentHTML('afterend', WIDGET_HTML);
      counter = header.nextElementSibling;
      changed = true;
    }
    // The order-value bar sits directly above the app's own savings progress bar.
    if (counter && !(counter.nextElementSibling && counter.nextElementSibling.hasAttribute('data-sweepstakes-aov'))) {
      ensureStyle();
      counter.insertAdjacentHTML('afterend', AOV_HTML);
      changed = true;
    }
    if (changed && window.CM_SWEEPSTAKES_REFRESH_ENTRIES) window.CM_SWEEPSTAKES_REFRESH_ENTRIES();
  }

  function trySetup() {
    var target = document.querySelector('#slidecarthq');
    if (!target) return false;
    new MutationObserver(injectIntoDrawer).observe(target, { childList: true, subtree: true });
    injectIntoDrawer();
    return true;
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (trySetup()) return;
    // SlideCart creates #slidecarthq itself, asynchronously, sometime after
    // its script loads — watch for it to appear as a body child, once.
    var bodyObserver = new MutationObserver(function () {
      if (trySetup()) bodyObserver.disconnect();
    });
    bodyObserver.observe(document.body, { childList: true });
  });

})();

